import React, { useMemo } from "react";
import { FiFileText, FiPrinter, FiDownload } from "react-icons/fi";

function BillSummary({
    products,
    shippingCharge = 0,
    otherCharge = 0,
    roundOff = 0,
}) {

    const summary = useMemo(() => {
        let subtotal = 0;
        let gstAmount = 0;
        let discountAmount = 0;
        let totalQty = 0;
        const gstSummary = {};

        products.forEach((item) => {
            const qty = Number(item.quantity || 0);
            const price = Number(item.purchase_price || 0);
            const gst = Number(item.gst_percentage || 0);
            const discount = Number(item.discount || 0);

            const amount = qty * price;
            const gstValue = (amount * gst) / 100;
            const discountValue = (amount * discount) / 100;

            // Bill Summary
            subtotal += amount;
            totalQty += qty;
            gstAmount += gstValue;
            discountAmount += discountValue;

            // GST Summary
            if (!gstSummary[gst]) {
                gstSummary[gst] = {
                    taxable: 0,
                    gst: 0,
                };
            }

            gstSummary[gst].taxable += amount;
            gstSummary[gst].gst += gstValue;
        });
        const grandTotal =
            subtotal +
            gstAmount -
            discountAmount +
            Number(shippingCharge || 0) +
            Number(otherCharge || 0) +
            Number(roundOff || 0);

        return {
            subtotal,
            gstAmount,
            discountAmount,
            grandTotal,
            totalQty,
            totalProducts: products.length,
            gstSummary,
        };
    }, [products, shippingCharge, otherCharge, roundOff]);

    // Helper function for number to words
    const numberToWords = (num) => {
        const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        if (num === 0) return 'Zero';
        if (num < 20) return a[num];
        if (num < 100) return b[Math.floor(num / 10)] + ' ' + a[num % 10];
        if (num < 1000) return a[Math.floor(num / 100)] + ' Hundred ' + numberToWords(num % 100);
        if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand ' + numberToWords(num % 1000);
        return 'Number too large';
    };
    return (
        <div className="row mt-3 justify-content-end mt-4">

            <div className="col-xl-6 col-lg-6 col-md-6 mb-3">

                <table className="table table-bordered table-hover align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th colSpan="3" className="text-center">
                                GST Summary
                            </th>
                        </tr>

                        <tr>
                            <th>GST%</th>
                            <th>Taxable</th>
                            <th>GST</th>
                        </tr>

                    </thead>

                    <tbody>
                        {Object.keys(summary.gstSummary).length === 0 ? (
                            <tr>
                                <td colSpan="3" className="text-center">
                                    No Data
                                </td>
                            </tr>
                        ) : (
                            <>
                                {Object.entries(summary.gstSummary).map(([rate, value]) => (
                                    <tr key={rate}>
                                        <td>{rate}%</td>
                                        <td>₹ {value.taxable.toFixed(2)}</td>
                                        <td>₹ {value.gst.toFixed(2)}</td>
                                    </tr>
                                ))}

                                <tr className="fw-bold">
                                    <td>Total</td>
                                    <td>₹ {summary.subtotal.toFixed(2)}</td>
                                    <td>₹ {summary.gstAmount.toFixed(2)}</td>
                                </tr>
                            </>
                        )}
                    </tbody>

                </table>

            </div>

            {/* Bill Summary */}

            <div className="col-xl-6 col-lg-6 col-md-6 mb-3">

                <table className="table table-bordered table-hover align-middle mb-0">

                    <thead className="table-light">

                        <tr>

                            <th colSpan="2" className="text-center">
                                Bill Summary
                            </th>

                        </tr>

                    </thead>

                    <tbody>
                        <tr>
                            <td>Sub Total</td>
                            <td className="text-end">
                                ₹ {summary.subtotal.toFixed(2)}
                            </td>
                        </tr>

                        <tr>
                            <td>GST</td>
                            <td className="text-end">
                                ₹ {summary.gstAmount.toFixed(2)}
                            </td>
                        </tr>

                        <tr>
                            <td>Discount</td>
                            <td className="text-end">
                                ₹ {summary.discountAmount.toFixed(2)}
                            </td>
                        </tr>

                        <tr>
                            <td>Shipping</td>
                            <td className="text-end">
                                ₹ {Number(shippingCharge).toFixed(2)}
                            </td>
                        </tr>

                        <tr>
                            <td>Other Charges</td>
                            <td className="text-end">
                                ₹ {Number(otherCharge).toFixed(2)}
                            </td>
                        </tr>

                        <tr>
                            <td>Round Off</td>
                            <td className="text-end">
                                ₹ {Number(roundOff).toFixed(2)}
                            </td>
                        </tr>

                        <tr className="table-success fw-bold">
                            <td>Grand Total</td>
                            <td className="text-end">
                                ₹ {summary.grandTotal.toFixed(2)}
                            </td>
                        </tr>
                    </tbody>

                </table>

            </div>

        </div>
    );
}

export default BillSummary;